import React from 'react';
import P from 'prop-types';
import cx from 'classnames';
import { graphql, compose } from 'react-apollo';
import { Redirect, withRouter } from 'react-router-dom';

import { ThemeContext } from 'components/App/';
import Button from 'components/Button/';
import Icon from 'components/Icon/';
import Label from 'components/Label/';
import Sidebar from 'components/Sidebar/';
import CreateOpportunityButton from 'components/CreateOpportunityButton/';
import PartnershipStatus from '../group/PartnershipStatus';
import { OPPORTUNITIES_LIST_QUERY } from 'gql/queries';
import ensureLoggedIn from 'utils/ensureLoggedIn';
import displayLoading from 'utils/displayLoading';
import config from 'config';
import './OpportunitiesList.scss';

const OpportunitiesList = ({ data, location }) => {
  const { group } = data.store;
  // TODO: Render Error Page
  if (!group) return null;

  const { id, draft, name, opportunities } = group;

  if (draft === true) {
    return <Redirect to={`/update-group/${id}`} />;
  }

  const referrer = `management-portal${location.pathname}`;

  return (
    <ThemeContext.Consumer>
      {({ VRCId, name: VRCName, contact, clientRegion, hideRequestPartnership }) => {
        return (
          <div className="OpportunitiesList">
            <div className="OpportunitiesList-main">
              <h2 className="OpportunitiesList-heading">{name} opportunities</h2>
              {!hideRequestPartnership && (
                <div>
                  <PartnershipStatus className="mb-4 mt-4" requesterId={id} requesteeId={VRCId} longMessage />
                </div>
              )}
              <h6 className="OpportunitiesList-subheading">
                When you manage or edit your opportunities you will be redirected to the Be Collective platform which
                powers our volunteer portal. You shouldn't need to enter your details again, but if you are prompted
                please enter the email address and password you created when you registered on our portal.
              </h6>
              <div>
                <CreateOpportunityButton VRCId={VRCId} referrer={referrer} groupId={id} clientRegion={clientRegion} />
              </div>
              <div className="OpportunitiesList-list">
                {opportunities.map(o => {
                  const isDraft = o.status === 'draft';
                  return (
                    <div className={cx('OpportunitiesList-list-item', { 'is-published': !isDraft })} key={o.id}>
                      <div className="OpportunitiesList-list-item-name">
                        {o.name || '<no title>'}
                        {isDraft && <Label className="ml-2">Draft</Label>}
                        {Array.isArray(o.campaigns) &&
                          o.campaigns.length > 0 &&
                          o.campaigns.map(c => <Label className="ml-2">{c.name}</Label>)}
                      </div>
                      <div className="OpportunitiesList-list-item-action">
                        {!isDraft && (
                          <Button
                            color="transparent"
                            onClick={() => {
                              window.open(`${config.WEB_URL}/manage/groups/${id}/opportunities/${o.id}/preview?ignoreRegionGuess=true`);
                            }}
                          >
                            <Icon className="mr-2" name="cog" color="primary" />
                            <span>Manage</span>
                          </Button>
                        )}
                        <Button
                          color="transparent"
                          onClick={() => {
                            window.location.href = `${config.WEB_URL}/opportunities/${o.id}/wizard?referrer=${referrer}&ignoreRegionGuess=true`;
                          }}
                        >
                          <Icon className="mr-2" name="pencil" color="primary" />
                          <span>Edit</span>
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="OpportunitiesList-aside">
              <Sidebar groupId={id} />
            </div>
          </div>
        );
      }}
    </ThemeContext.Consumer>
  );
};

OpportunitiesList.propTypes = {
  data: P.shape({
    store: P.shape({
      group: P.shape({
        id: P.string.isRequired,
        opportunities: P.arrayOf(
          P.shape({
            id: P.string.isRequired,
            status: P.string.isRequired,
            name: P.string,
          }),
        ).isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
};

export default compose(
  graphql(OPPORTUNITIES_LIST_QUERY, {
    options: ({ match }) => ({
      variables: {
        id: match.params.id,
      },
      fetchPolicy: 'network-only',
    }),
  }),
  ensureLoggedIn,
  withRouter,
  displayLoading(),
)(OpportunitiesList);
